import React, { useEffect, useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { Modal } from "@mui/material";
import MainBtn from "../../main/main_btn/MainBtn";
import "./style.scss";
import { DispatchProps, StateProps } from "../../../store";
import { mapDispatchToProps } from "../../../store/dispatcher";
import { connect } from "react-redux";
import { Device } from "../../../lib/Device";
import {
  SCHEDULE_STANDBY,
  isTemperatureScheduleValue,
} from "../../../lib/planningSchedule";

type SelectedBand = number;

const PlanningModal = ({
  device,
  open,
  close,
  planning,
  updateDevice,
}: {
    device: Device;
    open: boolean;
    close: any;
    planning: number[][];
    updateDevice: any;
  }) => {
  const { t } = useTranslation("settings");
  const [loading, setLoading] = useState(false);
  const [selectedBand, setSelectedBand] = useState<SelectedBand>(0);

  useEffect(() => {
    if (open) setSelectedBand(0);
  }, [open]);

  const tempToRange: Record<string, string> = {
    cold: `${device.t_cold}°C - ${device.t_lukewarm}°C`,
    warm: `${device.t_lukewarm}°C - ${device.t_hot}°C`,
    hot: `${device.t_hot}°C - ${device.t_very_hot}°C`,
    very_hot: `${device.t_very_hot}°C +`,
  };

  const applySelectionToSchedule = () => {
    const assignedValue = isTemperatureScheduleValue(selectedBand)
      ? selectedBand
      : SCHEDULE_STANDBY;

    const updated = planning.map((day) =>
      day.map((hour) => {
        if (hour < 0) {
          return assignedValue;
        }
        return hour;
      })
    );

    setLoading(true);
    updateDevice({
      id: parseInt(device.id ? device.id.toString() : "0"),
      data: { schedule: updated },
    }).then(() => {
      setLoading(false);
      close();
    });
  };

  const temperatureIds = ["cold", "warm", "hot", "very_hot"] as const;

  return (
    <Modal
      className="soliseco-modal planning-modal"
      open={open}
      onClose={close}
    >
      <div className="modal-center">
        <div className="modal-header">
          <div className="close-container" onClick={close}>
            <CloseOutlined />
          </div>
          <span className="title">{t("plan_modal_title")}</span>
        </div>
        <div className="modal-content">
          <div className="temp-container">
            {temperatureIds.map((_tempKey, index) => (
              <div
                role="button"
                tabIndex={0}
                onClick={() => setSelectedBand(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setSelectedBand(index);
                }}
                key={index}
                className={`temp-block heat-${index} ${
                  selectedBand === index ? "selected" : ""
                }`}
              >
                <span>{t(temperatureIds[index])}</span>
                <span className="temp-range">{tempToRange[temperatureIds[index]]}</span>
              </div>
            ))}
            <div
              role="button"
              tabIndex={0}
              className={`temp-block temp-block-full heat-standby ${
                selectedBand === SCHEDULE_STANDBY ? "selected" : ""
              }`}
              onClick={() => setSelectedBand(SCHEDULE_STANDBY)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  setSelectedBand(SCHEDULE_STANDBY);
              }}
            >
              <span>{t("schedule_standby")}</span>
              <span className="temp-range">{t("schedule_standby_hint")}</span>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <MainBtn title={t("cancel")} color="secondary" onClick={close} />
          <MainBtn
            title={t("confirm")}
            loading={loading}
            onClick={applySelectionToSchedule}
          />
        </div>
      </div>
    </Modal>
  );
};

export default connect<StateProps, DispatchProps>(
  null,
  mapDispatchToProps
)(PlanningModal);
